import CustomSelect from '@components/FormFields/CustomSelect';
import { Button, Card, IconButton } from '@hyperflake/react-ui-library';
import { CrossIcon, PlusIcon } from '@icon/index';
import { FormHelperText, Label } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import http from 'httpConfig/http';
import useCctvCameraAddForm from 'modules/cctv-cameras/hooks/useCctvCameraAddForm';
import FormikDatePicker from 'modules/shared/components/FormFields/FormikDatePicker';
import RequiredMark from 'modules/shared/components/FormFields/RequiredMark';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import { getResellerIdFromRedux } from 'modules/shared/helpers/shared.utils';
import moment from 'moment';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ICctvCameraAddForm {}

const CctvCameraAddForm = (props: ICctvCameraAddForm) => {
    const { clientId, projectId } = useParams();
    const addCctvCameraAddForm = useCctvCameraAddForm(clientId!, projectId!);
    const navigate = useNavigate();

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(
        ['clients', clientId, 'projects', projectId, 'cc-cameras', 'storage_names'],
        async () => {
            const { data } = await http.get(
                `${
                    import.meta.env.VITE_APP_API_URL
                }/${getResellerIdFromRedux()}/clients/${clientId}/projects/${projectId}/cameras/storage-names`
            );
            return data;
        }
    );

    const transformData = (values: any) => ({
        ...values,
        installationDate: moment(values.installationDate).format('YYYYMMDD'),
    });

    const handleSubmit = async (values: any) => {
        try {
            const tranformedData = transformData(values);
            await addCctvCameraAddForm.submit(tranformedData);
            toast.success('CCTV Camera added successfully');
            navigate(`/clients/${clientId}/projects/${projectId}/cctv-cameras`);
        } catch (e: any) {
            console.log(e);
            toast.error(e?.data?.message || 'Oops something went wrong.');
        }
    };
    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [{ label: 'Select Storage', value: '' }, ...storages.map((val: string) => ({ label: val, value: val }))];
    }, [storages]);

    return (
        <PageTemplate>
            <PageHeading className="!mb-3 sm:!mb-4">Add Live Camera</PageHeading>

            <Formik
                initialValues={addCctvCameraAddForm.initialValues}
                enableReinitialize
                validationSchema={addCctvCameraAddForm.schema}
                onSubmit={handleSubmit}
            >
                {({ values, errors }) => {
                    return (
                        <Form>
                            {/* BASIC DETAILS */}
                            <Card className="mt-4">
                                <Card.Header>Basic Details</Card.Header>
                                <Card.Body>
                                    {/* Grid: 1 col on mobile, 2 cols on sm+, 12-col on md+ */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4 md:gap-6">
                                        {/* Name */}
                                        <div className="md:col-span-6 min-w-0">
                                            <Label>
                                                Name <RequiredMark />
                                            </Label>
                                            <FormikInput type="text" name="name" className="w-full" />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="name" />
                                            </FormHelperText>
                                        </div>

                                        {/* Installation Date */}
                                        <div className="md:col-span-6 min-w-0">
                                            <Label>
                                                Installation Date <RequiredMark />
                                            </Label>
                                            <FormikDatePicker name="installationDate" maxDate={new Date()} />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="installationDate" />
                                            </FormHelperText>
                                        </div>

                                        {/* QLive Unit */}
                                        <div className="md:col-span-6 min-w-0">
                                            <Label>
                                                QLive Unit <RequiredMark />
                                            </Label>
                                            <div className="flex gap-2">
                                                <FormikInput name="qLiveUnit" className="w-full" />
                                            </div>
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="qLiveUnit" />
                                            </FormHelperText>
                                        </div>

                                        {/* Storage */}
                                        <div className="md:col-span-6 min-w-0">
                                            <Label>
                                                Storage <RequiredMark />
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                <CustomSelect name="storage" options={storageOption} />
                                                {storageIsLoading && <CircularProgress size={14} />}
                                            </div>
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="storage" />
                                            </FormHelperText>
                                        </div>

                                        <div className="md:col-span-6 min-w-0">
                                            <Label>
                                                Timezone <RequiredMark />
                                            </Label>
                                            <div className="flex gap-2">
                                                <FormikSelect
                                                    name="timezone"
                                                    options={addCctvCameraAddForm.timezoneOption}
                                                    showSearch
                                                />
                                            </div>
                                            <FormHelperText variant={'error'}>
                                                <ErrorMessage name="timezone" />
                                            </FormHelperText>
                                        </div>

                                        {/* Is Primary */}
                                        <div className="sm:col-span-2 md:col-span-12 flex items-center gap-3 mt-1">
                                            <Label htmlFor="isPrimary" className="mb-0 font-medium text-grayscale-900">
                                                Is Primary
                                            </Label>
                                            <FormikSwitch color="success" name="isPrimary" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* CHANNEL DETAILS */}
                            <Card className="mt-4">
                                <Card.Header>Channel Details</Card.Header>
                                <Card.Body>
                                    <FieldArray
                                        name="channels"
                                        render={(arrayHelpers) => (
                                            <div className="space-y-4 sm:space-y-5 md:space-y-6">
                                                {values.channels.map((_, index) => {
                                                    const isAvailable = values.channels[index]?.ptzControl?.isAvailable;

                                                    return (
                                                        <div
                                                            key={index}
                                                            className="rounded-xl border p-3 sm:p-4 md:p-5"
                                                        >
                                                            {/* Remove Button (top-right) */}
                                                            <div className="flex justify-end">
                                                                <IconButton
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                    size="sm"
                                                                    color="primary"
                                                                    className="!p-2"
                                                                    disabled={values.channels.length === 1}
                                                                    title={
                                                                        values.channels.length === 1
                                                                            ? 'At least one channel is required'
                                                                            : 'Remove channel'
                                                                    }
                                                                    aria-label="Remove channel"
                                                                >
                                                                    <CrossIcon />
                                                                </IconButton>
                                                            </div>

                                                            {/* Fields Grid */}
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4 md:gap-6">
                                                                {/* Channel Number */}
                                                                <div className="md:col-span-6 min-w-0">
                                                                    <Label>
                                                                        Channel Number <RequiredMark />
                                                                    </Label>
                                                                    <FormikInput
                                                                        name={`channels.${index}.number`}
                                                                        placeholder="e.g., 1"
                                                                        className="w-full"
                                                                        min={0}
                                                                    />
                                                                    <FormHelperText variant="error">
                                                                        <ErrorMessage
                                                                            name={`channels.${index}.number`}
                                                                        />
                                                                    </FormHelperText>
                                                                </div>

                                                                {/* Folder */}
                                                                {/* <div className="md:col-span-6 min-w-0">
                                                                    <Label>
                                                                        Folder 
                                                                    </Label>
                                                                    <FormikInput
                                                                        name={`channels.${index}.folder`}
                                                                        placeholder="Enter folder path/name"
                                                                        className="w-full"
                                                                    />
                                                                    <FormHelperText variant="error">
                                                                        <ErrorMessage
                                                                            name={`channels.${index}.folder`}
                                                                        />
                                                                    </FormHelperText>
                                                                </div> */}

                                                                {/* HLS URL */}
                                                                <div className="md:col-span-6 min-w-0">
                                                                    <Label>
                                                                        HLS URL <RequiredMark />
                                                                    </Label>
                                                                    <FormikInput
                                                                        name={`channels.${index}.hlsUrl`}
                                                                        placeholder="https://.../index.m3u8"
                                                                        className="w-full"
                                                                    />
                                                                    <FormHelperText variant="error">
                                                                        <ErrorMessage
                                                                            name={`channels.${index}.hlsUrl`}
                                                                        />
                                                                    </FormHelperText>
                                                                </div>

                                                                {/* WebRTC URL */}
                                                                <div className="md:col-span-12 min-w-0">
                                                                    <Label>WebRTC URL (optional)</Label>
                                                                    <FormikInput
                                                                        name={`channels.${index}.webrtcUrl`}
                                                                        placeholder="webrtc:// or https signaling URL"
                                                                        className="w-full"
                                                                    />
                                                                    <FormHelperText variant="error">
                                                                        <ErrorMessage
                                                                            name={`channels.${index}.webrtcUrl`}
                                                                        />
                                                                    </FormHelperText>
                                                                </div>

                                                                {/* PTZ Toggle */}
                                                                <div className="md:col-span-4 flex items-center gap-2">
                                                                    <Label className="mb-0">PTZ Available</Label>
                                                                    <FormikSwitch
                                                                        name={`channels.${index}.ptzControl.isAvailable`}
                                                                    />
                                                                </div>

                                                                {/* PTZ Device ID (Conditional) */}
                                                                {isAvailable && (
                                                                    <div className="md:col-span-8 min-w-0">
                                                                        <Label>
                                                                            PTZ Device ID <RequiredMark />
                                                                        </Label>
                                                                        <FormikInput
                                                                            name={`channels.${index}.ptzControl.deviceId`}
                                                                            placeholder="Enter PTZ device ID"
                                                                            className="w-full"
                                                                        />
                                                                        <FormHelperText variant="error">
                                                                            <ErrorMessage
                                                                                name={`channels.${index}.ptzControl.deviceId`}
                                                                            />
                                                                        </FormHelperText>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Add Channel Button */}
                                                <div>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() =>
                                                            arrayHelpers.push({
                                                                number: '',
                                                                hlsUrl: '',
                                                                webrtcUrl: '',
                                                                folder: '',
                                                                ptzControl: { isAvailable: false, deviceId: '' },
                                                            })
                                                        }
                                                        size="sm"
                                                        color="primary"
                                                        className="mt-1 sm:mt-2"
                                                        aria-label="Add channel"
                                                    >
                                                        <PlusIcon />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        )}
                                    />
                                </Card.Body>
                            </Card>

                            {/* LOCATION */}
                            <Card className="mt-4">
                                <Card.Header>Location</Card.Header>
                                <Card.Body>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                                        <div className="min-w-0">
                                            <Label>Latitude</Label>
                                            <FormikInput type="text" name="location.latitude" className="w-full" />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="location.latitude" />
                                            </FormHelperText>
                                        </div>
                                        <div className="min-w-0">
                                            <Label>Longitude</Label>
                                            <FormikInput type="text" name="location.longitude" className="w-full" />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="location.longitude" />
                                            </FormHelperText>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Submit */}
                            <Button
                                type="submit"
                                size="lg"
                                className="mt-4 w-full sm:w-32"
                                disabled={addCctvCameraAddForm.isSubmitting}
                            >
                                {addCctvCameraAddForm.isSubmitting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    'Submit'
                                )}
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </PageTemplate>
    );
};

export default CctvCameraAddForm;
